/**
 * App-Wurzel: React Router + Refine + Stack Auth + MAESTRO-Design-Shell.
 *
 * Routing:
 *   /handler/*                    -> StackHandler (Sign-in/up etc., Stack Auth)
 *   /login                        -> gestylte Login-Seite
 *   /angebot/:token               -> OEFFENTLICHE Angebotsseite (KEIN Login!)
 *   alle uebrigen Routen          -> geschuetzt, in der AppShell (Sidebar/Topbar)
 */
import { Refine } from "@refinedev/core";
import routerBindings from "@refinedev/react-router-v6";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { StackProvider, StackTheme, StackHandler, useUser } from "@stackframe/react";
import { stackApp } from "./stack";
import { dataProvider } from "./providers/dataProvider";
import { authProvider } from "./providers/authProvider";
import { AppShell } from "./ui/AppShell";
import DashboardPage from "./pages/dashboard";
import InquiriesPage from "./pages/inquiries";
import EventsPage from "./pages/events";
import EventDetailPage from "./pages/event-detail";
import BuilderPage from "./pages/builder";
import CustomersPage from "./pages/customers";
import CustomerDetailPage from "./pages/customer-detail";
import CatalogPage from "./pages/catalog";
import KnowledgePage from "./pages/knowledge";
import MenuImportPage from "./pages/menu-import";
import PaketePage from "./pages/pakete";
import KartenPage from "./pages/karten";
import TasksPage from "./pages/tasks";
import PaymentsPage from "./pages/payments";
import CalendarPage from "./pages/calendar";
import SettingsPage from "./pages/settings";
import MeinKontoPage from "./pages/mein-konto";
import PublicOfferPage from "./pages/offer-public";
import PublicChatPage from "./pages/chat-public";
import { LoginPage } from "./pages/login";
import AcceptInvitePage from "./pages/accept-invite";

/** Stack-Auth-Handler-Seiten unter /handler/* (Props: app/location/fullPage). */
function HandlerRoutes() {
  const location = useLocation();
  return <StackHandler app={stackApp} location={location.pathname} fullPage />;
}

/** Schutz-Layout: leitet nicht authentifizierte Nutzer zur Anmeldung um. */
function ProtectedLayout() {
  useUser({ or: "redirect" });
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

// Stack-Auth-Seiten (Account, Login, Verify, Reset) im MAESTRO-CI: Akzent-Orange + Radius
// passend zur App. Zusammen mit lang="de-DE" oben erscheinen die Stack-Flows on-brand und deutsch
// statt als nacktes englisches Default-CI.
const STACK_THEME = {
  light: { primary: "#C2410C" },
  dark: { primary: "#E2551E" },
  radius: "9px",
};

export default function App() {
  return (
    <BrowserRouter>
      <StackProvider app={stackApp} lang="de-DE">
        <StackTheme theme={STACK_THEME}>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            routerProvider={routerBindings}
            resources={[
              { name: "inquiries", list: "/anfragen", meta: { label: "Anfragen" } },
              { name: "events", list: "/veranstaltungen", meta: { label: "Veranstaltungen" } },
              { name: "customers", list: "/kunden", meta: { label: "Kunden" } },
            ]}
            options={{ syncWithLocation: true, disableTelemetry: true }}
          >
            <Routes>
              <Route path="/handler/*" element={<HandlerRoutes />} />
              <Route path="/login" element={<LoginPage />} />
              {/* Einladung annehmen - erzwingt Login, braucht aber KEINE Tenant-Mitgliedschaft (ProtectedLayout waere hier falsch). */}
              <Route path="/einladung" element={<AcceptInvitePage />} />
              {/* Oeffentliche Kundenseite - bewusst ausserhalb des Schutz-Layouts. */}
              <Route path="/angebot/:token" element={<PublicOfferPage />} />
              <Route path="/chat" element={<PublicChatPage />} />
              <Route element={<ProtectedLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="/anfragen" element={<InquiriesPage />} />
                <Route path="/veranstaltungen" element={<EventsPage />} />
                <Route path="/veranstaltungen/:id" element={<EventDetailPage />} />
                <Route path="/veranstaltungen/:id/angebot" element={<BuilderPage />} />
                <Route path="/kalender" element={<CalendarPage />} />
                <Route path="/kunden" element={<CustomersPage />} />
                <Route path="/kunden/:id" element={<CustomerDetailPage />} />
                <Route path="/aufgaben" element={<TasksPage />} />
                <Route path="/katalog" element={<CatalogPage />} />
                <Route path="/katalog/import" element={<MenuImportPage />} />
                <Route path="/katalog/pakete" element={<PaketePage />} />
                <Route path="/katalog/karten" element={<KartenPage />} />
                <Route path="/wissen" element={<KnowledgePage />} />
                <Route path="/zahlungen" element={<PaymentsPage />} />
                <Route path="/einstellungen" element={<SettingsPage />} />
                <Route path="/mein-konto" element={<MeinKontoPage />} />
              </Route>
            </Routes>
          </Refine>
        </StackTheme>
      </StackProvider>
    </BrowserRouter>
  );
}
